/**
 * Recruiting (WALLIYO-0131), as the mockup draws it: the vacancies, and each
 * one's pipeline under it.
 *
 * The stages read left to right in the order somebody moves through them, and
 * a candidate's row says which one they are at rather than which column they
 * are in — a board would need every stage on screen at once, and there are
 * seven.
 */

import type { CandidateStage, JobCandidate, JobOpening } from "@wallyo/contracts/console/hr";
import { t, type MessageId } from "../../i18n";
import { formatDateTime } from "../../i18n/format";
import { ErrorNotice } from "../../shell/ErrorNotice";

export type RecruitingState =
  | { status: "loading"; data: RecruitingData | null }
  | { status: "ready"; data: RecruitingData }
  | { status: "failed"; data: RecruitingData | null; error: unknown };

export type RecruitingData = {
  openings: readonly JobOpening[];
  candidates: readonly JobCandidate[];
};

const STAGE_LABEL: Record<CandidateStage, MessageId> = {
  applied: "recruiting.stage.applied",
  screening: "recruiting.stage.screening",
  interview: "recruiting.stage.interview",
  offer: "recruiting.stage.offer",
  hired: "recruiting.stage.hired",
  declined: "recruiting.stage.declined",
  withdrawn: "recruiting.stage.withdrawn",
};

const STATUS_LABEL: Record<JobOpening["status"], MessageId> = {
  open: "recruiting.status.open",
  on_hold: "recruiting.status.onHold",
  filled: "recruiting.status.filled",
  closed: "recruiting.status.closed",
};

/** The stages somebody is still in the running for, in order. */
export const LIVE_STAGES: readonly CandidateStage[] = ["applied", "screening", "interview", "offer"];

const STAGE_TONE: Record<CandidateStage, string> = {
  applied: "pill",
  screening: "pill",
  interview: "pill warn",
  offer: "pill warn",
  hired: "pill ok",
  declined: "pill bad",
  withdrawn: "pill bad",
};

function Pipeline({
  candidates,
  onMove,
}: {
  candidates: readonly JobCandidate[];
  onMove: (candidate: JobCandidate) => void;
}) {
  if (candidates.length === 0) return <p className="foot">{t("recruiting.noCandidates")}</p>;
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>{t("recruiting.col.candidate")}</th>
            <th>{t("recruiting.col.reach")}</th>
            <th>{t("recruiting.col.stage")}</th>
            <th>{t("recruiting.col.applied")}</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {candidates.map((candidate) => {
            const out = candidate.stage === "declined" || candidate.stage === "withdrawn";
            return (
              <tr key={candidate.jobCandidateId}>
                <td>
                  <strong>{candidate.name}</strong>
                  {candidate.note ? <div className="foot">{candidate.note}</div> : null}
                </td>
                <td className="foot">
                  {candidate.phone ?? ""}
                  {candidate.phone && candidate.email ? <br /> : null}
                  {candidate.email ?? ""}
                </td>
                <td>
                  <span className={STAGE_TONE[candidate.stage]}>{t(STAGE_LABEL[candidate.stage])}</span>
                </td>
                <td>{formatDateTime(candidate.createdAt)}</td>
                <td className="num">
                  {/* Somebody who withdrew or was turned down is not moved on
                      from: that decision was made, and unmaking it silently
                      would lose it. They are added again instead. */}
                  {out ? null : (
                    <button className="btn quiet small" type="button" onClick={() => onMove(candidate)}>
                      {t("recruiting.move")}
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function RecruitingView(props: {
  state: RecruitingState;
  onOpenJob: () => void;
  onEditJob: (opening: JobOpening) => void;
  onAddCandidate: (opening: JobOpening) => void;
  onMove: (candidate: JobCandidate) => void;
  onRetry: () => void;
}) {
  const { state } = props;
  const data = state.data;

  return (
    <>
      <div className="hr-search">
        <div className="grow" />
        <button className="btn" type="button" onClick={props.onOpenJob} disabled={state.status !== "ready"}>
          {t("recruiting.openJob")}
        </button>
      </div>

      {state.status === "failed" ? (
        <div className="audit-error">
          <p className="foot">{t("hr.failed")}</p>
          <ErrorNotice error={state.error} />
          <div className="btns">
            <button className="btn" type="button" onClick={props.onRetry}>
              {t("hr.retry")}
            </button>
          </div>
        </div>
      ) : null}

      {!data && state.status === "loading" ? (
        <div className="empty" role="status">
          <span className="spinner" aria-hidden="true" />
          <span>{t("hr.loading")}</span>
        </div>
      ) : null}

      {data && data.openings.length === 0 ? <div className="empty">{t("recruiting.none")}</div> : null}

      {(data?.openings ?? []).map((opening) => {
        const closed = opening.status === "filled" || opening.status === "closed";
        return (
          <section className="card" key={opening.jobOpeningId}>
            <div className="hr-search">
              <div>
                <h2>{opening.title}</h2>
                <p className="foot">
                  {opening.teamName ?? t("recruiting.noTeam")} ·{" "}
                  {t("recruiting.liveCandidates", { count: String(opening.liveCandidates) })}
                </p>
              </div>
              <div className="grow" />
              <span className={closed ? "pill" : "pill ok"}>{t(STATUS_LABEL[opening.status])}</span>
              <button className="btn quiet small" type="button" onClick={() => props.onEditJob(opening)}>
                {t("recruiting.editJob")}
              </button>
              {closed ? null : (
                <button className="btn quiet small" type="button" onClick={() => props.onAddCandidate(opening)}>
                  {t("recruiting.addCandidate")}
                </button>
              )}
            </div>
            {opening.description ? <p className="foot">{opening.description}</p> : null}
            <Pipeline
              candidates={(data?.candidates ?? []).filter((each) => each.jobOpeningId === opening.jobOpeningId)}
              onMove={props.onMove}
            />
          </section>
        );
      })}
    </>
  );
}
